import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Footer from "../components/core-components/Footer";

import "./Home.css";

const heroSocialLinks = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/valovate_val",
    icon: "/assets/SVG-Icons/instagram.svg",
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/@Valovate",
    icon: "/assets/SVG-Icons/youtube.svg",
  },
  {
    label: "X",
    href: "https://x.com/valovate",
    icon: "/assets/SVG-Icons/x_twt.svg",
  },
  {
    label: "Discord",
    href: "https://discord.gg/GrCvYvqFVF",
    icon: "/assets/SVG-Icons/discord.svg",
  },
  {
    label: "Twitch",
    href: "https://www.twitch.tv/valovate_val",
    icon: "/assets/SVG-Icons/twitch.svg",
  },
];

const homeRailItems = [
  {
    key: "hero",
    label: "Home",
    hash: "#hero",
    type: "home",
  },
  {
    key: "about",
    label: "About Us",
    hash: "#about",
    icon: "/assets/SVG-Icons/about.svg",
    iconClassName: "ParallaxBoxes__railIcon--about",
  },
  {
    key: "features",
    label: "Team Functions",
    hash: "#features",
    icon: "/assets/SVG-Icons/team_tools.svg",
  },
];

const individualFeatureItems = [
  "Comprehensive Agent & Lineup Guides",
  "Save & Review Highlights & Favorites",
  "Personal Notes with Rich Formatting",
  "Integrated Calendar & Event Reminders",
  "Customizable Theme & Zoom Controls",
  "Access to Exclusive VOD Deep-Dive Tools",
];

const teamFeatureItems = [
  "All Individual features",
  "Collaborative Team Page & Strategy Sharing",
  "Shared Highlights, Notes & Notifications",
  "Timestamped VOD Reviews with Tagged Notes",
  "Focus Point Tracking & Team Progress Graphs",
  "Team Calendar & Role-Based Alerts",
  "Team Avaialbility Scheduler",
  "Manger overview of all your teams",
];

export default function Home() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("hero");

  const heroTitleRef = useRef(null);
  const heroSocialsRef = useRef(null);

  const aboutHeadingRef = useRef(null);
  const chrisRailRef = useRef(null);
  const ahzhuCardRef = useRef(null);
  const guideCardRef = useRef(null);

  const featuresSectionRef = useRef(null);
  const featureStageRef = useRef(null);
  const featureHeadingRef = useRef(null);
  const featuresAnchorRef = useRef(null);
  const individualBoxRef = useRef(null);
  const teamBoxRef = useRef(null);
  const pcAppCardRef = useRef(null);

  const [visibleIndividualCount, setVisibleIndividualCount] = useState(0);
  const [visibleTeamCount, setVisibleTeamCount] = useState(0);
  const activeSectionRef = useRef("hero");

  const scrollToHomeSection = (rawHash, smooth = true) => {
    const sectionId = (rawHash || "").replace("#", "");
    if (!sectionId) return false;

    const target = document.getElementById(sectionId);
    if (!target) return false;

    target.scrollIntoView({
      behavior: smooth ? "smooth" : "auto",
      block: "start",
    });

    return true;
  };

  useLayoutEffect(() => {
    const heroTitle = heroTitleRef.current;
    const heroSocials = heroSocialsRef.current;
    const aboutHeading = aboutHeadingRef.current;
    const chrisRail = chrisRailRef.current;
    const ahzhuCard = ahzhuCardRef.current;
    const guideCard = guideCardRef.current;
    const featuresSection = featuresSectionRef.current;
    const featureStage = featureStageRef.current;
    const featureHeading = featureHeadingRef.current;
    const individualBox = individualBoxRef.current;
    const teamBox = teamBoxRef.current;
    const pcAppCard = pcAppCardRef.current;

    if (
      !heroTitle ||
      !heroSocials ||
      !aboutHeading ||
      !chrisRail ||
      !ahzhuCard ||
      !guideCard ||
      !featuresSection ||
      !featureStage ||
      !featureHeading ||
      !individualBox ||
      !teamBox ||
      !pcAppCard
    ) {
      return undefined;
    }

    let animationFrameId = 0;
    let isMounted = true;
    let lastIndividualCount = -1;
    let lastTeamCount = -1;

    function clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }

    function getVisibilityProgress(element) {
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;

      const visibleTop = Math.max(rect.top, 0);
      const visibleBottom = Math.min(rect.bottom, viewportHeight);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);

      return clamp(visibleHeight / Math.max(rect.height, 1), 0, 1);
    }

    function getViewportMetrics(element) {
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const elementCenter = rect.top + (rect.height / 2);

      return {
        rect,
        viewportHeight,
        elementCenter,
      };
    }

    function updateHeroTitleAnimation() {
      const { rect, viewportHeight, elementCenter } = getViewportMetrics(heroTitle);

      const fadeStartPoint = viewportHeight * 0.28;
      const fadeEndPoint = -rect.height * 0.35;

      let scale = 1;
      let opacity = 1;

      if (elementCenter <= fadeStartPoint) {
        const fadeProgress = clamp(
          (fadeStartPoint - elementCenter) / (fadeStartPoint - fadeEndPoint),
          0,
          1
        );

        scale = 1 - (0.18 * fadeProgress);
        opacity = 1 - (0.92 * fadeProgress);
      }

      if (rect.bottom <= 0) {
        opacity = 0;
      }

      heroTitle.style.transform = `scale(${scale})`;
      heroTitle.style.opacity = `${opacity}`;
      heroSocials.style.transform = `scale(${scale})`;
      heroSocials.style.opacity = `${opacity}`;
    }

    function easeOutCubic(progress) {
      return 1 - Math.pow(1 - progress, 3);
    }

    function applyRevealAnimation(element, options = {}) {
      const rawVisibilityProgress = getVisibilityProgress(element);
      const revealDelay = options.revealDelay ?? 0;
      const visibilityProgress = clamp(
        (rawVisibilityProgress - revealDelay) / Math.max(1 - revealDelay, 0.0001),
        0,
        1
      );
      const easedProgress = easeOutCubic(visibilityProgress);

      const startX = options.startX ?? 0;
      const startY = options.startY ?? 0;
      const endX = options.endX ?? 0;
      const endY = options.endY ?? 0;
      const startScale = options.startScale ?? 0.9;
      const endScale = options.endScale ?? 1;
      const startOpacity = options.startOpacity ?? 0.2;
      const endOpacity = options.endOpacity ?? 1;

      const currentX = startX + ((endX - startX) * easedProgress);
      const currentY = startY + ((endY - startY) * easedProgress);
      const currentScale =
        startScale + ((endScale - startScale) * easedProgress);
      const currentOpacity =
        startOpacity + ((endOpacity - startOpacity) * easedProgress);

      element.style.transform =
        `translateX(${currentX}rem) translateY(${currentY}rem) scale(${currentScale})`;
      element.style.opacity = `${currentOpacity}`;
    }

    function updateCardsAnimation() {
      applyRevealAnimation(aboutHeading, {
        startX: -10,
        startY: -2,
        startScale: 0.92,
        startOpacity: 0.12,
        revealDelay: 0.24,
      });

      applyRevealAnimation(chrisRail, {
        startX: -14,
        startY: 0,
        startScale: 0.84,
        startOpacity: 0.18,
        revealDelay: 0.34,
      });

      applyRevealAnimation(ahzhuCard, {
        startX: 10,
        startY: 0,
        startScale: 0.9,
        startOpacity: 0.1,
        revealDelay: 0.48,
      });

      applyRevealAnimation(guideCard, {
        startX: 10,
        startY: 8,
        startScale: 0.9,
        startOpacity: 0.1,
        revealDelay: 0.64,
      });
    }

    function updateFeatureBoxesAnimation() {
      const sectionRect = featuresSection.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;

      const entryStart = viewportHeight * 0.95;
      const freezeTrigger = viewportHeight * 0.35;
      const releaseTrigger = -featuresSection.offsetHeight + (viewportHeight * 1.1);

      const entryProgress = clamp(
        (entryStart - sectionRect.top) / Math.max(entryStart - freezeTrigger, 1),
        0,
        1
      );

      const easedEntry = easeOutCubic(entryProgress);

      const isStageFrozen =
        sectionRect.top <= freezeTrigger && sectionRect.top >= releaseTrigger;

      if (!isStageFrozen) {
        const leftX = -12 + (12 * easedEntry);
        const rightX = 12 - (12 * easedEntry);
        const yOffset = 2 - (2 * easedEntry);
        const currentScale = 0.86 + ((1 - 0.86) * easedEntry);
        const currentOpacity = 0.18 + ((1 - 0.18) * easedEntry);
        const headingY = -2 + (2 * easedEntry);
        const headingScale = 0.92 + ((1 - 0.92) * easedEntry);
        const headingOpacity = 0.12 + ((1 - 0.12) * easedEntry);
        const pcAppY = 3.25 - (3.25 * easedEntry);
        const pcAppScale = 0.92 + ((1 - 0.92) * easedEntry);
        const pcAppOpacity = 0.12 + ((1 - 0.12) * easedEntry);

        featureHeading.style.transform =
          `translateY(${headingY}rem) scale(${headingScale})`;
        featureHeading.style.opacity = `${headingOpacity}`;

        individualBox.style.transform =
          `translateX(${leftX}rem) translateY(${yOffset}rem) scale(${currentScale})`;
        individualBox.style.opacity = `${currentOpacity}`;

        teamBox.style.transform =
          `translateX(${rightX}rem) translateY(${yOffset}rem) scale(${currentScale})`;
        teamBox.style.opacity = `${currentOpacity}`;
        pcAppCard.style.transform =
          `translateX(0rem) translateY(${pcAppY}rem) scale(${pcAppScale})`;
        pcAppCard.style.opacity = `${pcAppOpacity}`;
      } else {
        featureHeading.style.transform = "translateY(0rem) scale(1)";
        featureHeading.style.opacity = "1";
        individualBox.style.transform = "translateX(0rem) translateY(0rem) scale(1)";
        teamBox.style.transform = "translateX(0rem) translateY(0rem) scale(1)";
        pcAppCard.style.transform = "translateX(0rem) translateY(0rem) scale(1)";
        individualBox.style.opacity = "1";
        teamBox.style.opacity = "1";
        pcAppCard.style.opacity = "1";
      }

      const holdScrollDistance = Math.max(
        featuresSection.offsetHeight - viewportHeight,
        1
      );

      const holdProgress = clamp(
        (-sectionRect.top) / holdScrollDistance,
        0,
        1
      );

      const bulletStart = 0;
      const bulletEnd = 0.000;

      const bulletProgress =
        holdProgress <= bulletStart
          ? 0
          : clamp(
              (holdProgress - bulletStart) / (bulletEnd - bulletStart),
              0,
              1
            );

      const individualCount = Math.min(
        Math.floor(bulletProgress * (individualFeatureItems.length + 0.999)),
        individualFeatureItems.length
      );

      const teamCount = Math.min(
        Math.floor(bulletProgress * (teamFeatureItems.length + 0.999)),
        teamFeatureItems.length
      );

      if (individualCount !== lastIndividualCount) {
        lastIndividualCount = individualCount;
        setVisibleIndividualCount(individualCount);
      }

      if (teamCount !== lastTeamCount) {
        lastTeamCount = teamCount;
        setVisibleTeamCount(teamCount);
      }

    }

    function updateAnimations() {
      updateHeroTitleAnimation();
      updateCardsAnimation();
      updateFeatureBoxesAnimation();

      if (isMounted) {
        animationFrameId = window.requestAnimationFrame(updateAnimations);
      }
    }

    updateAnimations();

    return () => {
      isMounted = false;
      window.cancelAnimationFrame(animationFrameId);
    };
  }, []);

  useEffect(() => {
    const activeHash = window.location.hash || location.hash || "";

    if (activeHash.startsWith("#team-hub")) {
      navigate(
        {
          pathname: "/team-center/dashboard",
          search: location.search,
        },
        { replace: true }
      );
      return;
    }

    if (activeHash.startsWith("#guide-hub")) {
      navigate(
        {
          pathname: "/guide-library",
          search: location.search,
        },
        { replace: true }
      );
    }
  }, [location.hash, location.search, navigate]);

  useEffect(() => {
    const activeHash = window.location.hash || location.hash;
    if (!activeHash) return undefined;

    let timeoutId = 0;
    let attempts = 0;

    const scrollToSection = () => {
      const didScroll = scrollToHomeSection(activeHash, attempts > 0);
      if (didScroll) return;

      if (attempts < 14) {
        attempts += 1;
        timeoutId = window.setTimeout(scrollToSection, 90);
      }
    };

    timeoutId = window.setTimeout(scrollToSection, 30);

    return () => window.clearTimeout(timeoutId);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    let timeoutId = 0;

    const runHashScroll = (activeHash) => {
      let attempts = 0;

      const tryScroll = () => {
        const didScroll = scrollToHomeSection(activeHash, true);
        if (didScroll) return;

        if (attempts < 18) {
          attempts += 1;
          timeoutId = window.setTimeout(tryScroll, 90);
        }
      };

      timeoutId = window.setTimeout(tryScroll, 30);
    };

    const handleHashChange = () => {
      const activeHash = window.location.hash;
      if (!activeHash) return;
      runHashScroll(activeHash);
    };

    const handleWindowLoad = () => {
      const activeHash = window.location.hash;
      if (!activeHash) return;
      runHashScroll(activeHash);
    };

    handleWindowLoad();

    window.addEventListener("hashchange", handleHashChange);
    window.addEventListener("load", handleWindowLoad);
    window.addEventListener("pageshow", handleWindowLoad);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      window.removeEventListener("load", handleWindowLoad);
      window.removeEventListener("pageshow", handleWindowLoad);
      window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const trackedSections = [
      { anchorId: "hero", sectionId: "hero" },
      { anchorId: "about", sectionId: "about-section" },
      { anchorId: "features", sectionId: "features-section" },
    ];
    let frameId = 0;

    const updateActiveSection = () => {
      const marker = window.innerHeight * 0.34;
      let nextActive = "hero";

      trackedSections.forEach(({ anchorId, sectionId }) => {
        const element = document.getElementById(sectionId);
        if (!element) return;

        const rect = element.getBoundingClientRect();
        if (rect.top <= marker && rect.bottom >= marker) {
          nextActive = anchorId;
        }
      });

      if (activeSectionRef.current !== nextActive) {
        activeSectionRef.current = nextActive;
        setActiveSection(nextActive);
      }

      frameId = window.requestAnimationFrame(updateActiveSection);
    };

    frameId = window.requestAnimationFrame(updateActiveSection);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, []);

  return (
    <div className="ParallaxBoxes__root">
      <aside className="ParallaxBoxes__rail" aria-label="Home page section shortcuts">
        <div className="ParallaxBoxes__railStack">
          {homeRailItems.map((item) => {
            const isActive = activeSection === item.key;
            const content = (
              <>
                <span className="ParallaxBoxes__railIconWrap" aria-hidden="true">
                  {item.type === "home" ? (
                    <svg
                      viewBox="0 0 24 24"
                      className="ParallaxBoxes__railIcon ParallaxBoxes__railIcon--stroke"
                    >
                      <path
                        d="M4 10.5L12 4l8 6.5M7 9.5V20h10V9.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.9"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <img
                      src={item.icon}
                      alt=""
                      className={
                        item.iconClassName
                          ? `ParallaxBoxes__railIcon ${item.iconClassName}`
                          : "ParallaxBoxes__railIcon"
                      }
                    />
                  )}
                </span>
                <span className="ParallaxBoxes__railLabel">{item.label}</span>
              </>
            );

            return (
              <a
                key={item.key}
                href={`/${item.hash}`}
                onClick={(event) => {
                  if (window.location.pathname !== "/") return;

                  event.preventDefault();
                  if (window.location.hash !== item.hash) {
                    window.location.hash = item.hash;
                  } else {
                    scrollToHomeSection(item.hash, true);
                  }
                }}
                className={
                  isActive
                    ? "ParallaxBoxes__railItem ParallaxBoxes__railItem--active"
                    : "ParallaxBoxes__railItem"
                }
              >
                {content}
              </a>
            );
          })}
        </div>
      </aside>

      <section
        id="hero"
        className="ParallaxBoxes__parallaxSection"
        aria-label="Hero"
      >
        <div className="ParallaxBoxes__heroLogoWrap">
          <img
            src="/assets/valovate_logo_full.svg"
            alt="Valovate logo"
            className="ParallaxBoxes__heroLogo"
            draggable="false"
          />
        </div>

        <div className="ParallaxBoxes__heroTitleWrap">
          <div className="ParallaxBoxes__heroIntro">
            <h1 ref={heroTitleRef} className="ParallaxBoxes__heroTitle">
              Your all-in-one Valorant command center
            </h1>

            <div ref={heroSocialsRef} className="ParallaxBoxes__heroSocials">
              {heroSocialLinks.map((social) => (
                <a
                  key={social.label}
                  className="ParallaxBoxes__heroSocialLink"
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                >
                  <img
                    src={social.icon}
                    alt={social.label}
                    className="ParallaxBoxes__heroSocialIcon"
                  />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="ParallaxBoxes__delaySpacer" />
      </section>

      <section
        id="about-section"
        className="ParallaxBoxes__cardsSection lp3-about"
        aria-label="About Valovate"
      >
        <div
          id="about"
          className="ParallaxBoxes__aboutAnchor"
          aria-hidden="true"
        />
        <div
          ref={aboutHeadingRef}
          className="ParallaxBoxes__sectionHeading ParallaxBoxes__sectionHeading--about"
        >
          About Us
        </div>

        <div className="ParallaxBoxes__rowBoxes">
          <div ref={chrisRailRef} className="ParallaxBoxes__leftRail">
            <div className="card card-chris">
              <div className="card-inner">
                <div className="card-front">
                  <p className="Chris-About-Title-Styling">ChrislaBlitz</p>
                </div>
                <div className="card-back card-back--bio">
                  <div className="card-back-bio-scroll">
                    <p>
                      Hi, I&apos;m Chris, the creator of Valovate.com Project. This
                      site was born out of my deep love for Valorant and, more
                      specifically, its strategic depth. Like many players, I found
                      myself constantly searching for lineups, tactics, and ideas
                      across a scattered sea of guides, videos, and ever-growing
                      Google Docs. These tools were useful but lacked structure,
                      customizability, and a unified home.
                    </p>
                    <p>
                      I set out to build what I always wished existed: a centralized,
                      all-in-one hub for Valorant players of all levels. A place
                      where strategy is broken down simply, where agent and macro
                      playbooks are both accessible and in-depth, and where learning
                      and improvement feel organized, interactive, and collaborative.
                    </p>
                    <p>
                      With my background as a university Valorant team analyst, I
                      expanded the platform to go beyond solo learning. Teams and
                      coaches now have access to tools like highlights, favorites,
                      sharable notes, and even a VOD review system where timestamped
                      focus points can be tagged, rated, and tracked over time
                      turning subjective feedback into measurable growth.
                    </p>
                    <p>
                      From detailed coordinated playbooks to smart tools like team
                      calendars and VOD review systems, Valovate.com is designed to
                      support every kind of player whether you&apos;re aiming to climb
                      the ranked ladder or sharpen your team’s competitive edge.
                      Everything you need to grow, all in one structured, easy-to-use
                      platform.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="ParallaxBoxes__rightColumn">
            <div
              ref={ahzhuCardRef}
              className="ParallaxBoxes__rightCardSlot ParallaxBoxes__ahzhuRail"
            >
              <div className="card card-mayoishe">
                <div className="card-inner">
                  <div className="card-front">
                    <p className="Chris-About-Title-Styling">ahzhu</p>
                  </div>

                  <div className="card-back card-back--bio">
                    <div className="card-back-bio-scroll">
                      <p>
                        Ahzhu is an amazing young and talented designer who created
                        the logo, icon, and color design for this website. Huge
                        thanks to her for bringing Valovate’s visuals to life. If
                        you need design work, she delivers thoughtful, high-quality
                        results every time!
                        <a
                          href="https://x.com/azvwuu"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="Ahzhu-social-link"
                        >
                          @azvwuu on X
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              ref={guideCardRef}
              className="ParallaxBoxes__rightCardSlot ParallaxBoxes__guideSlot"
            >
              <div className="card card-guide">
                <div className="card-inner">
                  <div className="card-front card-back--thanks">
                    <h2 className="special-thanks-title">A Special Thanks</h2>
                    <p className="special-thanks-small-subtitle">
                      to all who helped along the way
                    </p>

                    <div className="thanks-list">
                      <ul>
                        <li>AlittlebitofCoconut</li>
                        <li>Bloodluster</li>
                        <li>Iapplepieguy</li>
                        <li>Kale</li>
                        <li>Lithium1k</li>
                        <li>livy</li>
                        <li>mayoishe</li>
                        <li>SharpnessV</li>
                        <li>Waffies</li>
                        <li>yellcwroses</li>
                        <li>Mel</li>
                        <li>swag</li>
                        <li>mindless</li>
                        <li>Apole</li>
                        <li>Alchemmy</li>
                        <li>Squidometer</li>
                        <li>Art3mka</li>
                        <li>Xylandir</li>
                        <li>...and many more &lt;3</li>
                      </ul>
                    </div>
                  </div>

                  <div className="card-back">
                    <img
                      src="/assets/valovate_logo_full.svg"
                      alt="Logo"
                      className="card-front-logo"
                    />
                    <h2>THE GUIDE</h2>
                    <p>
                      This guide centralizes all you Valorant Team Improvement
                      Desires!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        id="features-section"
        ref={featuresSectionRef}
        className="ParallaxBoxes__featuresSection"
        aria-label="Features"
      >
        <div
          id="features"
          ref={featuresAnchorRef}
          className="ParallaxBoxes__featuresAnchor"
          aria-hidden="true"
        />
        <div ref={featureStageRef} className="ParallaxBoxes__featureStage">
          <div className="ParallaxBoxes__featureStageInner">
            <div
              ref={featureHeadingRef}
              className="ParallaxBoxes__sectionHeading ParallaxBoxes__sectionHeading--features"
            >
              What Valovate offers
            </div>

            <div className="ParallaxBoxes__featureRow">
              <div
                ref={individualBoxRef}
                className="ParallaxBoxes__featureBox ParallaxBoxes__featureBox--left"
              >
                <div className="ParallaxBoxes__featureBoxInner">
                  <h3 className="ParallaxBoxes__featureTitle">Individual Functions</h3>
                  <ul className="ParallaxBoxes__featureList">
                    {individualFeatureItems.map((item, index) => (
                      <li
                        key={item}
                        className={
                          index < visibleIndividualCount
                            ? "ParallaxBoxes__featureItem ParallaxBoxes__featureItem--visible"
                            : "ParallaxBoxes__featureItem"
                        }
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div
                ref={teamBoxRef}
                className="ParallaxBoxes__featureBox ParallaxBoxes__featureBox--right"
              >
                <div className="ParallaxBoxes__featureBoxInner">
                  <h3 className="ParallaxBoxes__featureTitle">Team Functions</h3>
                  <ul className="ParallaxBoxes__featureList">
                    {teamFeatureItems.map((item, index) => (
                      <li
                        key={item}
                        className={
                          index < visibleTeamCount
                            ? "ParallaxBoxes__featureItem ParallaxBoxes__featureItem--visible"
                            : "ParallaxBoxes__featureItem"
                        }
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="ParallaxBoxes__featureSpotlight">
              <article
                ref={pcAppCardRef}
                className="ParallaxBoxes__featureSpotlightCard"
              >
                <h3 className="ParallaxBoxes__featureSpotlightTitle">
                  Desktop App
                </h3>
                <p className="ParallaxBoxes__featureSpotlightCopy">
                  Make your life easier by using the Desktop App.
                </p>
                <p className="ParallaxBoxes__featureSpotlightCopy">
                  The Valovate App allows users to streamline their performance
                  and offers easier usage.
                </p>
                <p className="ParallaxBoxes__featureSpotlightCopy">
                  In addition to special perks such as directly recording their
                  gameplay in app with tons of customization such as:
                </p>
                <ul
                  className="ParallaxBoxes__featureSpotlightList"
                  aria-label="Desktop app feature list"
                >
                  <li>Recording in custom high quality</li>
                  <li>Recording with multiple audio tracks</li>
                  <li>Saving VODs locally (less cloud usage)</li>
                  <li>And much more!</li>
                </ul>
                <div className="ParallaxBoxes__featureSpotlightActionRow">
                  <button
                    type="button"
                    className="ParallaxBoxes__featureSpotlightDownloadButton"
                    onClick={() => navigate("/desktop-app/download")}
                    aria-label="Open desktop app download page"
                    title="Open desktop app download page"
                  >
                    <img
                      src="/assets/SVG-Icons/pc_app_download.svg"
                      alt=""
                      className="ParallaxBoxes__featureSpotlightDownloadIcon"
                    />
                  </button>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>


      <Footer />
    </div>
  );
}
